import { ApolloClient, ApolloProvider } from '@apollo/client';
import { CssBaseline, useMediaQuery } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { FC, Suspense, useEffect, useMemo, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { RouterProvider } from 'react-router-dom';

import { createApolloClient } from '../src/config/createApolloClient';
import { initialUserSettings, UserSettings } from '../src/config/userSettings';
import PageLoader from './components/Atoms/Loaders/PageLoader';
import { ProvideAuth } from './hooks/useAuth.hook';
import router from './pages/Router';
import { createAppTheme, customTheme } from './styles/appTheme';

const App: FC = () => {
  const [client, setClient] = useState<ApolloClient<unknown>>();

  const [settings, setSettings] = useState(initialUserSettings);

  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const isDarkMode = useMemo(() => settings.darkMode ?? prefersDarkMode, [prefersDarkMode, settings.darkMode]);
  const theme = useMemo(() => createAppTheme(isDarkMode), [isDarkMode]);

  useEffect(() => {
    createApolloClient().then((client) => setClient(client));
  }, []);

  if (client === undefined) return <PageLoader />;

  return (
    <UserSettings.Provider value={[settings, setSettings]}>
      <ThemeProvider theme={customTheme}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Toaster />
          <ApolloProvider client={client}>
            <ProvideAuth>
              <Suspense fallback={<PageLoader />}>
                <RouterProvider future={{ v7_startTransition: true }} router={router} />
              </Suspense>
            </ProvideAuth>
          </ApolloProvider>
        </ThemeProvider>
      </ThemeProvider>
    </UserSettings.Provider>
  );
};

export default App;
